import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChatCircle, PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [peerTyping, setPeerTyping] = useState(false);
  const scrollRef = useRef();
  const typingRef = useRef(0);

  const loadThreads = async () => {
    const r = await api.get("/messages/threads");
    setThreads(r.data);
    if (!active && r.data.length) setActive(r.data[0]);
  };
  const loadThread = async (peer) => {
    if (!peer) return;
    const r = await api.get(`/messages/thread/${peer.peer_id}`);
    setMsgs(r.data);
    setTimeout(() => scrollRef.current?.scrollTo({ top: 99999 }), 50);
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { loadThread(active); }, [active?.peer_id]);

  // Poll for new messages + typing
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const [t, s] = await Promise.all([
          api.get(`/messages/thread/${active.peer_id}`),
          api.get(`/messages/thread-status/${active.peer_id}`),
        ]);
        if (cancelled) return;
        setMsgs((prev) => (t.data.length !== prev.length ? t.data : prev));
        setPeerTyping(!!s.data.peer_typing);
      } catch (err) { console.warn("thread poll failed", err); }
    };
    const id = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, [active?.peer_id]);

  const onType = () => {
    const now = Date.now();
    if (now - typingRef.current > 2000 && active) {
      typingRef.current = now;
      api.post(`/messages/typing/${active.peer_id}`).catch(() => {});
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post("/messages", { receiver_id: active.peer_id, booking_id: active.booking_id, message: text.trim() });
      setText("");
      loadThread(active);
      loadThreads();
    } catch { toast.error("Could not send"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <ChatCircle size={28} className="text-blue-600" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Messages</h1>
      </div>
      <div className="grid md:grid-cols-3 gap-4 h-[560px]">
        <div className="card-tactile p-3 overflow-y-auto">
          {threads.length === 0 && <div className="text-slate-500 p-6 text-center text-sm">No conversations yet. Message a company from a booking.</div>}
          {threads.map((t) => (
            <button key={t.peer_id} data-testid={`thread-${t.peer_id}`} onClick={() => setActive(t)}
              className={`w-full text-left p-3 rounded-md ${active?.peer_id === t.peer_id ? "bg-blue-50 border border-blue-200" : "hover:bg-slate-50"}`}>
              <div className="font-semibold text-slate-900 truncate">{t.peer_name || "User"}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">{t.peer_role}</div>
              <div className="text-sm text-slate-600 truncate mt-0.5">{t.last_message}</div>
            </button>
          ))}
        </div>
        <div className="md:col-span-2 card-tactile flex flex-col overflow-hidden">
          {active ? (
            <>
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <div className="font-semibold text-slate-900">{active.peer_name}</div>
                <div className="label-caps flex items-center gap-2">
                  <span>{active.peer_role}</span>
                  {peerTyping && (
                    <span data-testid="peer-typing" className="text-blue-600 normal-case tracking-normal font-semibold flex items-center gap-1">
                      <TypingDots /> typing…
                    </span>
                  )}
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
                {msgs.length === 0 && <div className="text-center text-slate-500 text-sm py-10">Start the conversation.</div>}
                {msgs.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-3.5 py-2 rounded-md text-sm ${mine ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-900"}`}>
                        <div className="whitespace-pre-wrap">{m.message}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-blue-100" : "text-slate-500"}`}>{new Date(m.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="p-3 border-t border-slate-200 flex gap-2">
                <Input data-testid="msg-input" value={text} onChange={(e)=>{ setText(e.target.value); onType(); }} placeholder="Type a message…" className="h-11 flex-1" />
                <Button data-testid="msg-send-btn" type="submit" className="bg-blue-600 hover:bg-blue-700 h-11"><PaperPlaneRight size={18} /></Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-500">Select a conversation</div>
          )}
        </div>
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex gap-0.5">
      <span className="w-1 h-1 rounded-full bg-blue-600 animate-pulse" style={{ animationDelay: "0ms" }} />
      <span className="w-1 h-1 rounded-full bg-blue-600 animate-pulse" style={{ animationDelay: "200ms" }} />
      <span className="w-1 h-1 rounded-full bg-blue-600 animate-pulse" style={{ animationDelay: "400ms" }} />
    </span>
  );
}
